import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { FiZap, FiX, FiRefreshCw, FiChevronLeft, FiChevronRight, FiUser, FiChevronDown, FiSearch, FiPlus, FiMail, FiPhone, FiLinkedin, FiGithub, FiFileText, FiGlobe, FiBriefcase, FiBookOpen } from "react-icons/fi";

import api from "@/services/api";
import Modal from "@/components/modal";
import { downloadResume } from "@/utils";
import { countryOptions, countryLabel } from "@/constants/countries";

const CONTRACT_OPTIONS = ["CDI", "CDD", "FREELANCE", "INTERNSHIP"];
const PAGE_SIZE = 25;
const MATCH_CAP = 1000;

function buildSearchBody(organizationId, filters, page, limitOverride) {
  const body = {
    organization_id: organizationId,
    role_candidate: true,
    page,
    limit: limitOverride ?? PAGE_SIZE,
  };
  if (filters?.skills?.length) body.skills = { include: filters.skills };
  if (filters?.countries?.length) body.countries = { include: filters.countries };
  if (filters?.cities?.length) body.cities = { include: filters.cities };
  if (filters?.languages?.length) body.languages = { include: filters.languages };
  return body;
}

function timeAgo(date) {
  if (!date) return "";
  const seconds = Math.floor((new Date() - new Date(date)) / 1000);
  const intervals = { y: 31536000, mo: 2592000, w: 604800, d: 86400, h: 3600, m: 60 };
  for (const [unit, s] of Object.entries(intervals)) {
    const n = Math.floor(seconds / s);
    if (n >= 1) return `${n}${unit} ago`;
  }
  return "just now";
}

function CountrySelect({ values, onChange }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const options = countryOptions();
  const filtered = options.filter((o) => o.label.toLowerCase().includes(search.toLowerCase()));
  const toggle = (code) => {
    if (values.includes(code)) onChange(values.filter((v) => v !== code));
    else onChange([...values, code]);
  };

  return (
    <div>
      <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-2">Country</div>
      <div className="flex flex-wrap gap-1.5 mb-1.5">
        {values.map((v) => (
          <span key={v} className="inline-flex items-center gap-1 px-2 py-1 bg-indigo-50 text-indigo-700 rounded-md text-xs font-medium">
            {countryLabel(v)}
            <button onClick={() => onChange(values.filter((x) => x !== v))} className="hover:text-indigo-900">
              <FiX className="w-3 h-3" />
            </button>
          </span>
        ))}
      </div>
      <div className="relative" ref={ref}>
        <button
          type="button"
          onClick={() => setOpen(!open)}
          className="w-full flex items-center justify-between px-2 py-1.5 text-xs border border-dashed border-gray-300 rounded-md hover:border-gray-400 transition-colors text-gray-500"
        >
          <span>+ Add country</span>
          <FiChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
        {open && (
          <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-hidden flex flex-col">
            <div className="relative border-b border-gray-100 p-2">
              <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-3.5 h-3.5" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search country..."
                autoFocus
                className="w-full pl-7 pr-2 py-1 text-xs border border-gray-200 rounded focus:border-indigo-400 focus:ring-0 outline-none"
              />
            </div>
            <div className="overflow-y-auto">
              {filtered.length === 0 ? (
                <div className="px-3 py-2 text-xs text-gray-400">No match</div>
              ) : (
                filtered.map((opt) => {
                  const selected = values.includes(opt.value);
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => toggle(opt.value)}
                      className={`w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 flex items-center justify-between ${selected ? "bg-indigo-50 text-indigo-700 font-medium" : "text-gray-700"}`}
                    >
                      <span>{opt.label}</span>
                      {selected && <FiX className="w-3 h-3" />}
                    </button>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ChipGroup({ label, values, onChange, placeholder }) {
  const [input, setInput] = useState("");
  const add = () => {
    const v = input.trim();
    if (!v) return;
    if (values.includes(v)) return setInput("");
    onChange([...values, v]);
    setInput("");
  };
  return (
    <div>
      <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-2">{label}</div>
      <div className="flex flex-wrap gap-1.5">
        {values.map((v) => (
          <span key={v} className="inline-flex items-center gap-1 px-2 py-1 bg-indigo-50 text-indigo-700 rounded-md text-xs font-medium">
            {v}
            <button onClick={() => onChange(values.filter((x) => x !== v))} className="hover:text-indigo-900">
              <FiX className="w-3 h-3" />
            </button>
          </span>
        ))}
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add();
            }
          }}
          onBlur={add}
          placeholder={placeholder}
          className="w-24 px-2 py-1 text-xs border border-dashed border-gray-300 rounded-md focus:border-indigo-400 focus:ring-0 outline-none"
        />
      </div>
    </div>
  );
}

export default function Pool({ job, setJob }) {
  const { id } = useParams();
  const [generating, setGenerating] = useState(false);
  const [filters, setFilters] = useState(job?.pool_filters || null);

  const [candidates, setCandidates] = useState([]);
  const [totalFiltered, setTotalFiltered] = useState(0);
  const [totalPool, setTotalPool] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [selectAllAcrossPages, setSelectAllAcrossPages] = useState(false);
  const [moving, setMoving] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });

  const [selectedUser, setSelectedUser] = useState(null);

  const saveTimer = useRef(null);
  const skipNextAutoSave = useRef(false);

  useEffect(() => {
    skipNextAutoSave.current = true;
    setFilters(job?.pool_filters || null);
  }, [job?.pool_filters]);

  useEffect(() => {
    if (!job?.organization_id) return;
    (async () => {
      try {
        const { ok, total } = await api.post("/user/search", {
          organization_id: job.organization_id,
          role_candidate: true,
          limit: 1,
          page: 1,
        });
        if (ok) setTotalPool(total || 0);
      } catch (e) {
        console.error(e);
      }
    })();
  }, [job?.organization_id]);

  const fetchCandidates = async (filtersOverride, pageOverride) => {
    if (!job?.organization_id) return;
    setLoading(true);
    try {
      const body = buildSearchBody(job.organization_id, filtersOverride ?? filters, pageOverride ?? page);
      const { ok, data, total } = await api.post("/user/search", body);
      if (ok) {
        setCandidates(data || []);
        setTotalFiltered(total || 0);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCandidates(filters, page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, job?.organization_id]);

  useEffect(() => {
    if (skipNextAutoSave.current) {
      skipNextAutoSave.current = false;
      return;
    }
    if (!filters) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        const { ok, data } = await api.put(`/job/${id}`, {
          organization_id: job.organization_id,
          pool_filters: filters,
        });
        if (ok) setJob(data);
        setPage(1);
        setSelectedIds(new Set());
        setSelectAllAcrossPages(false);
        fetchCandidates(filters, 1);
      } catch (e) {
        console.error(e);
      }
    }, 500);
    return () => saveTimer.current && clearTimeout(saveTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const handleGenerate = async () => {
    if (!job?.brief && !job?.description) {
      return toast.error("Add a brief to your job first");
    }
    setGenerating(true);
    try {
      const { ok, data } = await api.post(`/job/${id}/generate-pool-filters`);
      if (!ok) return toast.error("Failed to generate filters");
      setJob(data);
      toast.success("Filters generated from brief");
    } catch (e) {
      console.error(e);
      toast.error("Failed to generate filters");
    } finally {
      setGenerating(false);
    }
  };

  const updateFilter = (key, value) => {
    setFilters((prev) => ({ ...(prev || {}), [key]: value }));
  };

  const addOneToPipeline = async (user) => {
    const cid = (user._id || "").toString();
    const createRes = await api.post("/application", {
      job_id: id,
      organization_id: job.organization_id,
      candidate_id: cid,
      candidate_name: `${user.firstname || ""} ${user.lastname || ""}`.trim(),
      candidate_type: "pool",
      status: "APPLIED",
      source: "POOL",
      candidate_has_applied: false,
    });
    if (!createRes.ok || !createRes.data?._id) return false;
    // POST /application is idempotent — if a stale POOL record exists, flip it to APPLIED.
    if (createRes.data.status !== "APPLIED") {
      await api.put(`/application/${createRes.data._id}`, {
        status: "APPLIED",
        organization_id: job.organization_id,
      });
    }
    return true;
  };

  const handleMoveSingle = async (user) => {
    try {
      const ok = await addOneToPipeline(user);
      if (!ok) return toast.error("Failed to add to pipeline");
      toast.success(`${user.firstname || "Candidate"} added to pipeline`);
      setSelectedUser(null);
    } catch (e) {
      console.error(e);
      toast.error("Failed to add to pipeline");
    }
  };

  const resolveTargets = async () => {
    if (selectAllAcrossPages) {
      const body = buildSearchBody(job.organization_id, filters, 1, MATCH_CAP);
      const { ok, data } = await api.post("/user/search", body);
      if (!ok) return [];
      return data || [];
    }
    return candidates.filter((c) => selectedIds.has((c._id || "").toString()));
  };

  const handleBulkMove = async () => {
    if (moving) return;
    if (!job?.organization_id) return;

    setMoving(true);
    setProgress({ done: 0, total: 0 });

    try {
      const targets = await resolveTargets();
      if (!targets.length) {
        toast.error("No candidates selected");
        return;
      }
      setProgress({ done: 0, total: targets.length });

      let success = 0;
      for (let i = 0; i < targets.length; i++) {
        try {
          const ok = await addOneToPipeline(targets[i]);
          if (ok) success += 1;
        } catch (e) {
          console.error(e);
        }
        setProgress({ done: i + 1, total: targets.length });
      }

      toast.success(`${success} candidate${success === 1 ? "" : "s"} added to pipeline`);
      setSelectedIds(new Set());
      setSelectAllAcrossPages(false);
    } catch (e) {
      console.error(e);
      toast.error("Failed to move candidates");
    } finally {
      setMoving(false);
    }
  };

  const togglePageSelectAll = (checked) => {
    if (checked) {
      const next = new Set(selectedIds);
      candidates.forEach((c) => next.add((c._id || "").toString()));
      setSelectedIds(next);
    } else {
      const pageIds = new Set(candidates.map((c) => (c._id || "").toString()));
      const next = new Set([...selectedIds].filter((x) => !pageIds.has(x)));
      setSelectedIds(next);
      setSelectAllAcrossPages(false);
    }
  };

  const toggleOne = (cid) => {
    const next = new Set(selectedIds);
    if (next.has(cid)) {
      next.delete(cid);
      setSelectAllAcrossPages(false);
    } else {
      next.add(cid);
    }
    setSelectedIds(next);
  };

  const pageIds = candidates.map((c) => (c._id || "").toString());
  const pageAllSelected = pageIds.length > 0 && pageIds.every((cid) => selectedIds.has(cid));
  const selectedCount = selectAllAcrossPages ? totalFiltered : selectedIds.size;

  const hasFilters = filters && filters.generated_at;
  const isEmpty = !filters || !filters.generated_at;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / PAGE_SIZE));
  const hasBrief = !!(job?.brief || job?.description);

  if (isEmpty) {
    return (
      <div className="w-full px-6 py-16 flex items-start justify-center">
        <div className="w-full max-w-md bg-white border border-gray-200 rounded-xl p-8 text-center">
          <div className="mx-auto w-12 h-12 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center mb-4">
            <FiZap className="w-6 h-6" />
          </div>
          <h2 className="text-base font-semibold text-gray-900 mb-1">Find candidates from your pool</h2>
          <p className="text-sm text-gray-500 mb-6">We'll generate filters from your brief and you can move matching candidates straight into your pipeline.</p>
          <button
            onClick={handleGenerate}
            disabled={generating || !hasBrief}
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {generating ? (
              <>
                <FiRefreshCw className="w-4 h-4 animate-spin" />
                Generating filters...
              </>
            ) : (
              <>
                <FiZap className="w-4 h-4" />
                Generate filters from brief
              </>
            )}
          </button>
          {!hasBrief && <p className="text-[11px] text-gray-400 mt-3">Add a brief to your job first.</p>}
        </div>
      </div>
    );
  }

  return (
    <div className="w-full px-6 py-6">
      <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-[320px_1fr] gap-6">
        {/* Left — Filters */}
        <div className="bg-white border border-gray-200 rounded-xl p-5 h-fit">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-900">Filters</h3>
            {hasFilters && <span className="text-[10px] font-medium text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded">From brief</span>}
          </div>

          <div className="space-y-5">
            <ChipGroup label="Skills (must-have)" values={filters.skills || []} onChange={(v) => updateFilter("skills", v)} placeholder="+ Add" />
            <CountrySelect values={filters.countries || []} onChange={(v) => updateFilter("countries", v)} />
            <ChipGroup label="Cities" values={filters.cities || []} onChange={(v) => updateFilter("cities", v)} placeholder="+ City" />
            <ChipGroup label="Languages" values={filters.languages || []} onChange={(v) => updateFilter("languages", v)} placeholder="+ Lang" />

            <div>
              <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-2">Min. experience (years)</div>
              <input
                type="number"
                min={0}
                value={filters.min_years_experience ?? ""}
                onChange={(e) => updateFilter("min_years_experience", e.target.value === "" ? undefined : Number(e.target.value))}
                className="w-24 px-2 py-1 text-sm border border-gray-200 rounded-md focus:border-indigo-400 focus:ring-0 outline-none"
              />
            </div>

            <div>
              <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-2">Contract</div>
              <select
                value={filters.contract || ""}
                onChange={(e) => updateFilter("contract", e.target.value || undefined)}
                className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-md focus:border-indigo-400 focus:ring-0 outline-none"
              >
                <option value="">Any</option>
                {CONTRACT_OPTIONS.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>

            <button
              onClick={handleGenerate}
              disabled={generating}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 text-xs font-medium text-gray-600 hover:text-gray-900 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              <FiRefreshCw className={`w-3.5 h-3.5 ${generating ? "animate-spin" : ""}`} />
              {generating ? "Regenerating..." : "Regenerate from brief"}
            </button>
          </div>
        </div>

        {/* Right — Candidates */}
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <CandidatesPanel
            candidates={candidates}
            loading={loading}
            totalFiltered={totalFiltered}
            totalPool={totalPool}
            page={page}
            totalPages={totalPages}
            onPrev={() => setPage((p) => Math.max(1, p - 1))}
            onNext={() => setPage((p) => Math.min(totalPages, p + 1))}
            filters={filters}
            onSelect={setSelectedUser}
            selectedIds={selectedIds}
            pageAllSelected={pageAllSelected}
            onToggleOne={toggleOne}
            onTogglePageAll={togglePageSelectAll}
            selectAllAcrossPages={selectAllAcrossPages}
            onSelectAllAcross={() => setSelectAllAcrossPages(true)}
            onClearSelection={() => {
              setSelectedIds(new Set());
              setSelectAllAcrossPages(false);
            }}
            selectedCount={selectedCount}
            onMove={handleBulkMove}
            moving={moving}
            progress={progress}
          />
        </div>
      </div>

      <UserDrawer user={selectedUser} onClose={() => setSelectedUser(null)} onMove={handleMoveSingle} filters={filters} />
    </div>
  );
}

function CandidatesPanel({
  candidates,
  loading,
  totalFiltered,
  totalPool,
  page,
  totalPages,
  onPrev,
  onNext,
  filters,
  onSelect,
  selectedIds,
  pageAllSelected,
  onToggleOne,
  onTogglePageAll,
  selectAllAcrossPages,
  onSelectAllAcross,
  onClearSelection,
  selectedCount,
  onMove,
  moving,
  progress,
}) {
  const showAcrossPagesBar = pageAllSelected && totalFiltered > candidates.length && !selectAllAcrossPages;

  return (
    <>
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
        <div>
          <div className="text-lg font-semibold text-gray-900">
            {loading ? <span className="text-gray-300">…</span> : totalFiltered.toLocaleString()} <span className="text-sm font-normal text-gray-500">candidates match</span>
          </div>
          <div className="text-xs text-gray-400">Out of {totalPool.toLocaleString()} in your pool</div>
        </div>

        <div className="flex items-center gap-3">
          {totalFiltered > 0 && (
            <div className="flex items-center gap-2 text-xs text-gray-600">
              <button onClick={onPrev} disabled={page === 1} className="p-1 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed">
                <FiChevronLeft className="w-4 h-4" />
              </button>
              <span>
                Page {page} of {totalPages}
              </span>
              <button onClick={onNext} disabled={page >= totalPages} className="p-1 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed">
                <FiChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
          <button
            onClick={onMove}
            disabled={moving || selectedCount === 0}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-xs font-medium hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {moving ? (
              <>
                <FiRefreshCw className="w-3.5 h-3.5 animate-spin" />
                Moving {progress.done}/{progress.total}…
              </>
            ) : (
              <>
                <FiPlus className="w-3.5 h-3.5" />
                Move to pipeline{selectedCount > 0 ? ` (${selectedCount})` : ""}
              </>
            )}
          </button>
        </div>
      </div>

      {/* Bulk progress bar */}
      {moving && progress.total > 0 && (
        <div className="px-5 py-2 border-b border-gray-100">
          <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div className="h-full bg-indigo-500 transition-all" style={{ width: `${(progress.done / progress.total) * 100}%` }} />
          </div>
        </div>
      )}

      {/* Select-all-across-pages banner */}
      {showAcrossPagesBar && (
        <div className="px-5 py-2 bg-indigo-50 border-b border-indigo-100 text-xs text-indigo-800 flex items-center justify-center gap-2">
          <span>All {candidates.length} on this page selected.</span>
          <button onClick={onSelectAllAcross} className="font-semibold underline hover:no-underline">
            Select all {totalFiltered.toLocaleString()} across pages
          </button>
        </div>
      )}
      {selectAllAcrossPages && (
        <div className="px-5 py-2 bg-indigo-50 border-b border-indigo-100 text-xs text-indigo-800 flex items-center justify-center gap-2">
          <span>All {totalFiltered.toLocaleString()} candidates across pages selected.</span>
          <button onClick={onClearSelection} className="font-semibold underline hover:no-underline">
            Clear selection
          </button>
        </div>
      )}

      {/* Body */}
      {candidates.length === 0 && !loading ? (
        <div className="py-16 text-center text-gray-500 text-sm">
          <FiUser className="w-8 h-8 text-gray-300 mx-auto mb-2" />
          No candidates match these filters
        </div>
      ) : (
        <table className="w-full table-fixed divide-y divide-gray-200">
          <colgroup>
            <col style={{ width: "40px" }} />
            <col style={{ width: "20%" }} />
            <col style={{ width: "22%" }} />
            <col style={{ width: "12%" }} />
            <col style={{ width: "18%" }} />
            <col style={{ width: "18%" }} />
            <col style={{ width: "10%" }} />
          </colgroup>
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left">
                <input
                  type="checkbox"
                  checked={pageAllSelected}
                  onChange={(e) => onTogglePageAll(e.target.checked)}
                  className="w-3.5 h-3.5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                  aria-label="Select all on this page"
                />
              </th>
              <th className="px-3 py-2 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Name</th>
              <th className="px-3 py-2 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Last experience</th>
              <th className="px-3 py-2 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">City</th>
              <th className="px-3 py-2 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Skills</th>
              <th className="px-3 py-2 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Languages</th>
              <th className="px-3 py-2 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Joined</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-100">
            {candidates.map((c) => {
              const cid = (c._id || "").toString();
              const isSelected = selectAllAcrossPages || selectedIds.has(cid);
              const lastExp = (c.experiences || [])[0];
              const matchSkillSet = new Set((filters?.skills || []).map((s) => s.toLowerCase()));
              return (
                <tr key={c._id} onClick={() => onSelect && onSelect(c)} className="hover:bg-gray-50 transition-colors cursor-pointer">
                  <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={isSelected}
                      disabled={selectAllAcrossPages}
                      onChange={() => onToggleOne(cid)}
                      className="w-3.5 h-3.5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer disabled:cursor-not-allowed"
                      aria-label={`Select ${c.firstname || ""} ${c.lastname || ""}`}
                    />
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="w-7 h-7 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-[11px] font-bold shrink-0">
                        {(c.firstname?.[0] || "") + (c.lastname?.[0] || "")}
                      </div>
                      <div className="text-sm font-medium text-gray-900 truncate">
                        {c.firstname} {c.lastname}
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-2.5 text-sm text-gray-700">
                    {lastExp ? (
                      <>
                        <div className="font-medium truncate">{lastExp.title}</div>
                        <div className="text-[11px] text-gray-400 truncate">{lastExp.company}</div>
                      </>
                    ) : (
                      <span className="text-gray-300">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-sm text-gray-700 truncate">{c.city || <span className="text-gray-300">—</span>}</td>
                  <td className="px-3 py-2.5">
                    <div className="flex flex-nowrap items-center gap-1 overflow-hidden">
                      {(c.skills || []).slice(0, 2).map((s) => {
                        const isMatch = matchSkillSet.has((s.name || "").toLowerCase());
                        return (
                          <span
                            key={s.name}
                            className={`text-[10px] px-1.5 py-0.5 rounded whitespace-nowrap shrink-0 truncate max-w-[70px] ${isMatch ? "bg-indigo-100 text-indigo-700 font-semibold" : "bg-gray-100 text-gray-600"}`}
                          >
                            {s.name}
                          </span>
                        );
                      })}
                      {(c.skills?.length || 0) > 2 && <span className="text-[10px] text-gray-400 shrink-0">+{c.skills.length - 2}</span>}
                    </div>
                  </td>
                  <td className="px-3 py-2.5">
                    {(() => {
                      const langs = (c.languages || []).map((l) => l.name).filter(Boolean);
                      if (langs.length === 0) return <span className="text-gray-300">—</span>;
                      const visible = langs.slice(0, 2).join(", ");
                      const rest = langs.length - 2;
                      return (
                        <div className="text-xs text-gray-600 truncate" title={langs.join(", ")}>
                          {visible}
                          {rest > 0 && <span className="text-gray-400"> +{rest}</span>}
                        </div>
                      );
                    })()}
                  </td>
                  <td className="px-3 py-2.5 text-xs text-gray-500 whitespace-nowrap">
                    {c.created_at ? timeAgo(c.created_at) : <span className="text-gray-300">—</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </>
  );
}

function UserDrawer({ user, onClose, onMove, filters }) {
  if (!user) return null;
  const matchSkillSet = new Set((filters?.skills || []).map((s) => s.toLowerCase()));

  return (
    <Modal isOpen={!!user} onClose={onClose} className="w-full max-w-2xl">
      <div className="max-h-[85vh] overflow-y-auto">
        {/* Header */}
        <div className="px-6 py-5 border-b border-gray-100 flex items-center gap-4">
          <div className="w-14 h-14 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-lg font-bold shrink-0">
            {(user.firstname?.[0] || "") + (user.lastname?.[0] || "")}
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-semibold text-gray-900 truncate">
              {user.firstname} {user.lastname}
            </h2>
            <div className="flex items-center gap-1.5 mt-1.5">
              {user.email && (
                <a href={`mailto:${user.email}`} className="p-1.5 rounded-md bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors" title={user.email}>
                  <FiMail className="w-3.5 h-3.5" />
                </a>
              )}
              {user.phone && (
                <a href={`tel:${user.phone}`} className="p-1.5 rounded-md bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors" title={user.phone}>
                  <FiPhone className="w-3.5 h-3.5" />
                </a>
              )}
              {user.linkedin && (
                <a href={user.linkedin} target="_blank" rel="noopener noreferrer" className="p-1.5 rounded-md bg-blue-100 text-blue-600 hover:bg-blue-200 transition-colors" title="LinkedIn">
                  <FiLinkedin className="w-3.5 h-3.5" />
                </a>
              )}
              {user.github && (
                <a href={user.github} target="_blank" rel="noopener noreferrer" className="p-1.5 rounded-md bg-purple-100 text-purple-600 hover:bg-purple-200 transition-colors" title="GitHub">
                  <FiGithub className="w-3.5 h-3.5" />
                </a>
              )}
              {user.portfolio && (
                <a href={user.portfolio} target="_blank" rel="noopener noreferrer" className="p-1.5 rounded-md bg-green-100 text-green-600 hover:bg-green-200 transition-colors" title="Portfolio">
                  <FiGlobe className="w-3.5 h-3.5" />
                </a>
              )}
              {user.resume_url && (
                <button type="button" onClick={() => downloadResume(user)} className="p-1.5 rounded-md bg-red-100 text-red-600 hover:bg-red-200 transition-colors" title="Resume">
                  <FiFileText className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-6">
          {user.about && (
            <section>
              <h3 className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-2">About</h3>
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{user.about}</p>
            </section>
          )}

          {(user.city || user.country) && (
            <section className="text-sm text-gray-700">
              <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Location: </span>
              {[user.city, countryLabel(user.country)].filter(Boolean).join(", ")}
            </section>
          )}

          {(user.skills || []).length > 0 && (
            <section>
              <h3 className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-2">Skills</h3>
              <div className="flex flex-wrap gap-1.5">
                {user.skills.map((s, i) => {
                  const isMatch = matchSkillSet.has((s.name || "").toLowerCase());
                  return (
                    <span
                      key={`${s.name}-${i}`}
                      className={`text-xs px-2 py-0.5 rounded ${isMatch ? "bg-indigo-100 text-indigo-700 font-semibold" : "bg-gray-100 text-gray-700"}`}
                    >
                      {s.name}
                    </span>
                  );
                })}
              </div>
            </section>
          )}

          {(user.languages || []).length > 0 && (
            <section>
              <h3 className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-2">Languages</h3>
              <div className="flex flex-wrap gap-1.5">
                {user.languages.map((l, i) => (
                  <span key={`${l.name}-${i}`} className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-700">
                    {l.name}
                    {l.level ? ` (${l.level})` : ""}
                  </span>
                ))}
              </div>
            </section>
          )}

          {(user.experiences || []).length > 0 && (
            <section>
              <h3 className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                <FiBriefcase className="w-3.5 h-3.5" />
                Experience
              </h3>
              <ul className="space-y-3">
                {user.experiences.map((e, i) => (
                  <li key={i} className="text-sm">
                    <div className="font-medium text-gray-900">{e.title || "—"}</div>
                    <div className="text-gray-600">{[e.company, e.location].filter(Boolean).join(" · ")}</div>
                    {(e.from || e.to || e.current) && (
                      <div className="text-[11px] text-gray-400 mt-0.5">
                        {e.from ? new Date(e.from).getFullYear() : "?"} – {e.current ? "Present" : e.to ? new Date(e.to).getFullYear() : "?"}
                      </div>
                    )}
                    {e.description && <div className="text-xs text-gray-600 mt-1 line-clamp-3">{e.description}</div>}
                  </li>
                ))}
              </ul>
            </section>
          )}

          {(user.education || []).length > 0 && (
            <section>
              <h3 className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                <FiBookOpen className="w-3.5 h-3.5" />
                Education
              </h3>
              <ul className="space-y-3">
                {user.education.map((ed, i) => (
                  <li key={i} className="text-sm">
                    <div className="font-medium text-gray-900">{ed.degree || ed.field_of_study || "—"}</div>
                    <div className="text-gray-600">{ed.institution}</div>
                    {(ed.from || ed.to || ed.current) && (
                      <div className="text-[11px] text-gray-400 mt-0.5">
                        {ed.from ? new Date(ed.from).getFullYear() : "?"} – {ed.current ? "Present" : ed.to ? new Date(ed.to).getFullYear() : "?"}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between bg-gray-50 rounded-b-lg sticky bottom-0">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors">
            Close
          </button>
          <button
            onClick={() => onMove(user)}
            className="flex items-center gap-1.5 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors"
          >
            <FiPlus className="w-4 h-4" />
            Add to pipeline
          </button>
        </div>
      </div>
    </Modal>
  );
}
